import { useEffect, useState } from 'react';
import Script from 'next/script';
import { Heading, Paragraph, StyledWordEmphasizer } from '@components';
import { sendPupillaryDistance } from '@services/poms/operations/send-pupillary-distance';
import { useTranslation, VEERO_URL } from '@utils/index';
import { useLoadVeero } from '@services/veero';

type PdToolModalProps = {
	open: boolean;
	orderNumber?: string;
	setPdToolInit: (open: boolean) => void;
	setPdToolHasError: (open: boolean) => void;
	pdValueHandler?: (pd: number) => void;
	hasScriptLoaded?: boolean;
	onScriptLoaded?: () => void;
	setPdNeeded?: (pdNeeded: boolean) => void;
	isException?: boolean;
};

const getTranslatedTexts = translator => {
	return {
		pdReceived: translator('pd-received'),
		pdReceivedSuccessfully: (pd: string) => translator('pd-received-successfully', { pd }),
		errorNotification: translator('error-notification'),
		pdDatabaseIssue: (pd: string) => translator('pd-database-issue', { pd }),
		measuringToolIssue: translator('measuring-tool-issue'),
		errorUploadingPdMessage: translator('error-uploading-pd-meesage'),
		close: translator('close-uppercase'),
	};
};

const PdToolModal = ({
	open,
	orderNumber,
	setPdToolInit,
	setPdToolHasError,
	pdValueHandler,
	hasScriptLoaded = false,
	onScriptLoaded,
	setPdNeeded,
	isException,
}: PdToolModalProps) => {
	const [pd, setPd] = useState(null);
	const [isPomsSubmitSuccessful, setPomsSubmitSuccessful] = useState(false);
	const [hasAppError, setAppError] = useState(false);
	const [pdScriptLoaded, setPdScriptLoaded] = useState(hasScriptLoaded);
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	let message = null;

	const pdToolInitialized = useLoadVeero({ setAppError, pdScriptLoaded });

	const handleVeeroResults = results => {
		if (results?.mono_pd) {
			const pdValue = results?.mono_pd * 1000;
			setPd(pdValue);
			pdValueHandler && pdValueHandler(pdValue);
		}
	};

	const config = {
		eyeSizeCallback: handleVeeroResults,
		eyeSizeExitCallback: handleVeeroResults,
		vendorID: process.env.NEXT_PUBLIC_VEERO_ID,
		initialMeasurementIdx: 4,
		mode: 0,
		glassesStart: false,
		launchingFromEmail: true,
	};

	useEffect(() => {
		setPdToolInit(pdToolInitialized);
		setPdToolHasError(hasAppError);
		// https://veero.gitbook.io/veero-eyesize-documentation/XTCTOPPO4ayo1DXkVt0J/eyesize/browser-sdk/site-integration

		if (pdToolInitialized && !pd && !hasAppError && open) {
			window?.EyeSize?.launch(config);
		}
	}, [pdToolInitialized, hasAppError, open, pd]);

	useEffect(() => {
		const handleEyeSizeAvailable = () => {
			if (window?.EyeSize?.emailMeasurementReceived()) {
				window?.EyeSize?.launch(config);
			}
		};
		document.addEventListener('eyeSizeAvailable', handleEyeSizeAvailable);
		return () => {
			document.removeEventListener('eyeSizeAvailable', handleEyeSizeAvailable);
		};
	}, []);

	useEffect(() => {
		const submitPd = async () => {
			if (isException) {
				setPomsSubmitSuccessful(true);
				setAppError(false);
				setPdNeeded(false);
			} else {
				try {
					await sendPupillaryDistance(orderNumber, pd);
					setPomsSubmitSuccessful(true);
					setAppError(false);
					setPdNeeded(false);
				} catch (e) {
					setAppError(true);
				}
			}
		};
		if (orderNumber && pd) {
			submitPd();
		}
	}, [pd, orderNumber, setPdNeeded, isException]);

	if (isPomsSubmitSuccessful) {
		message = (
			<>
				<Heading tag={'h2'}>{translations.pdReceived}</Heading>
				<Paragraph>
					<StyledWordEmphasizer
						text={translations.pdReceivedSuccessfully(pd)}
						emphasizedWords={String(pd)}
						styledTexts={<b>{pd}</b>}
					/>
				</Paragraph>
			</>
		);
	}

	if (hasAppError) {
		message = (
			<>
				<Heading tag={'h2'}>{translations.errorNotification}</Heading>
				<Paragraph>
					{pd ? (
						<StyledWordEmphasizer
							text={translations.pdDatabaseIssue(pd)}
							emphasizedWords={String(pd)}
							styledTexts={<b>{pd}</b>}
						/>
					) : (
						translations.measuringToolIssue
					)}
					<StyledWordEmphasizer
						text={translations.errorUploadingPdMessage}
						emphasizedWords={'hello@paireyewear.com'}
						styledTexts={<a href='mailto:hello@paireyewear.com'>hello@paireyewear.com</a>}
					/>
				</Paragraph>
			</>
		);
		console.error('Error uploading PD', message);
	}

	const handleOnLoad = () => {
		setPdScriptLoaded(true);
		onScriptLoaded && onScriptLoaded();
	};

	const veeroScript = (
		<Script
			defer
			id='veero-location'
			src={`${VEERO_URL}?b=${process.env.NEXT_PUBLIC_VEERO_ID}`}
			onLoad={() => handleOnLoad()}
		/>
	);

	return (
		<>
			{veeroScript}
			<div id='veero-eyefit' />
		</>
	);
};

PdToolModal.displayName = 'PdToolModal';
export default PdToolModal;
